// Modelo del formulario de promo de referido: esquema zod en espejo de `ReferralPromotionInput` y
// conversión entre lo que se teclea (pesos, %) y el body (centavos, puntos base).
import { z } from 'zod';
import { mxDay, mxDayEndUtc, mxDayStartUtc } from '@/lib/dateHelpers';
import type {
    CreateSalePromotionRequest,
    DiscountType,
    ProductCategory,
    ProductTier,
    PromotionPaymentOption,
    ReferralChannel,
    ReferralPromotionConfig,
    ReferralPromotionInput,
    ReferralReward,
    ReferralRewardInput,
    ReferralRewardType,
    ReferralTrigger,
    SalePromotion,
    SalePromotionLeversInput,
} from '../api/promotionsContract';
import {
    CASH_METHODS,
    FINANCIERS,
    triggerHasPurchase,
    type CashMethod,
    type Financier,
    type PayWith,
    type ReferralRole,
} from './referralPromotionOptions';
import { PAYMENT_OPTION_VALUES } from './salePromotionFormOptions';
import {
    discountValue,
    FORM_MESSAGES,
    fromHundredths,
    isCount,
    isMoney,
    toCents,
    type Issue,
} from './salePromotionForm';
import { budgetCents, isBudgetAmount } from './budgetText';
import {
    costCenterError,
    costCenterForCreate,
    costCenterForUpdate,
    costCenterText,
} from '@/shared/lib/costCenter/costCenter';

const MIN_REPEAT = 2;

export const REFERRAL_FORM_MESSAGES = {
    name: FORM_MESSAGES.name,
    money: FORM_MESSAGES.money,
    percent: FORM_MESSAGES.percent,
    count: FORM_MESSAGES.count,
    validUntil: FORM_MESSAGES.validUntil,
    range: FORM_MESSAGES.range,
    repeat: `Pon un número entero de ${String(MIN_REPEAT)} o más.`,
    needsPurchase: 'Al abrir su cuenta no hay compra: el premio solo puede ser un bono fijo.',
} as const;

const discountType = z.enum(['AMOUNT', 'PERCENTAGE']);
const rewardType = z.enum(['FIXED_BONUS', 'PERCENT_BONUS', 'COUPON', 'SALE_DISCOUNT']);
const rewardForm = z.object({
    type: rewardType,
    amount: z.string(),
    percent: z.string(),
    maxAmount: z.string(),
    couponType: discountType,
    couponValue: z.string(),
    couponDays: z.string(),
});

export const referralPromotionFormSchema = z
    .object({
        channel: z.enum(['SALE', 'CREDIT']),
        name: z.string(),
        customerText: z.string(),
        trigger: z.enum([
            'FIRST_PURCHASE',
            'ACCOUNT_OPENED',
            'ANY_PURCHASE',
            'NTH_PURCHASE',
            'CREDIT_ACTIVATED',
            'FIRST_INSTALLMENT',
            'INSTALLMENT_STREAK',
        ]),
        purchaseNumber: z.string(),
        installmentStreak: z.string(),
        payWith: z.enum(['ANY', 'CASH', 'SUREKEEP', 'OTHER', 'CUSTOM']),
        customPaymentOptions: z.array(z.enum(PAYMENT_OPTION_VALUES)),
        cashMethods: z.array(z.enum(CASH_METHODS)),
        financiers: z.array(z.enum(FINANCIERS)),
        minAmount: z.string(),
        windowDays: z.string(),
        maxRewardsPerReferrer: z.string(),
        maxRewardsPerReferral: z.string(),
        maxReferredRewards: z.string(),
        productCategories: z.array(z.enum(['PHONE', 'SIM', 'MODEM', 'ACCESSORY', 'BUNDLE'])),
        tiers: z.array(z.enum(['LOW', 'MID', 'HIGH', 'PREMIUM'])),
        storeIds: z.array(z.string()),
        segmentIds: z.array(z.string()),
        requiresActiveSurekeepCredit: z.boolean(),
        referrerReward: rewardForm,
        referredEnabled: z.boolean(),
        referredReward: rewardForm,
        budgetMax: z.string(),
        validFrom: z.string(),
        permanent: z.boolean(),
        validUntil: z.string(),
        costCenterId: z.string(),
    })
    .superRefine((values, ctx) => {
        for (const [path, message] of referralFormIssues(values)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });
        }
    });

/** Premio de un rol tal como se teclea; solo se lee lo de su `type`. */
export interface ReferralRewardForm {
    type: ReferralRewardType;
    amount: string;
    percent: string;
    /** Tope del %; vacío = sin tope. */
    maxAmount: string;
    couponType: DiscountType;
    couponValue: string;
    couponDays: string;
}

export interface ReferralPromotionFormValues {
    channel: ReferralChannel;
    name: string;
    customerText: string;
    trigger: ReferralTrigger;
    purchaseNumber: string;
    installmentStreak: string;
    payWith: PayWith;
    /** Con «Contado»: vacío = cualquier medio de contado. */
    cashMethods: CashMethod[];
    /** Con «Otra financiera»: vacío = cualquiera de ellas. */
    financiers: Financier[];
    /** Las formas de pago guardadas que no caben en las opciones; solo se leen con `CUSTOM`. */
    customPaymentOptions: PromotionPaymentOption[];
    minAmount: string;
    windowDays: string;
    maxRewardsPerReferrer: string;
    /** Veces que se le paga a quien refiere por cada referido; entero ≥ 1. */
    maxRewardsPerReferral: string;
    /** Veces que se le paga o aplica el premio al referido; entero ≥ 1. */
    maxReferredRewards: string;
    productCategories: ProductCategory[];
    tiers: ProductTier[];
    storeIds: string[];
    segmentIds: string[];
    requiresActiveSurekeepCredit: boolean;
    referrerReward: ReferralRewardForm;
    referredEnabled: boolean;
    referredReward: ReferralRewardForm;
    budgetMax: string;
    validFrom: string;
    permanent: boolean;
    validUntil: string;
    /** Centro de costo de los premios que la promo paga a la wallet; vacío = sin asignar. */
    costCenterId: string;
}

const isPercent = (text: string) => isMoney(text) && Number(text) <= 100;
const isRepeat = (text: string) => isCount(text) && Number(text) >= MIN_REPEAT;
const optional = (text: string, ok: (t: string) => boolean) => text.trim() === '' || ok(text);

function rewardIssues(
    reward: ReferralRewardForm,
    role: ReferralRole,
    trigger: ReferralTrigger,
): Issue[] {
    if (!triggerHasPurchase(trigger) && reward.type !== 'FIXED_BONUS')
        return [[[role, 'type'], REFERRAL_FORM_MESSAGES.needsPurchase]];
    const out: Issue[] = [];
    const check = (ok: boolean, field: keyof ReferralRewardForm, message: string) => {
        if (!ok) out.push([[role, field], message]);
    };
    switch (reward.type) {
        case 'FIXED_BONUS':
            check(isMoney(reward.amount), 'amount', REFERRAL_FORM_MESSAGES.money);
            break;
        case 'PERCENT_BONUS':
        case 'SALE_DISCOUNT':
            check(isPercent(reward.percent), 'percent', REFERRAL_FORM_MESSAGES.percent);
            check(optional(reward.maxAmount, isMoney), 'maxAmount', REFERRAL_FORM_MESSAGES.money);
            break;
        case 'COUPON':
            check(
                reward.couponType === 'PERCENTAGE'
                    ? isPercent(reward.couponValue)
                    : isMoney(reward.couponValue),
                'couponValue',
                reward.couponType === 'PERCENTAGE'
                    ? REFERRAL_FORM_MESSAGES.percent
                    : REFERRAL_FORM_MESSAGES.money,
            );
            check(isCount(reward.couponDays), 'couponDays', REFERRAL_FORM_MESSAGES.count);
            break;
    }
    return out;
}

function referralFormIssues(v: ReferralPromotionFormValues): Issue[] {
    const issues: Issue[] = [];
    const check = (ok: boolean, path: string, message: string) => {
        if (!ok) issues.push([[path], message]);
    };
    check(v.name.trim() !== '', 'name', REFERRAL_FORM_MESSAGES.name);
    if (v.trigger === 'NTH_PURCHASE')
        check(isRepeat(v.purchaseNumber), 'purchaseNumber', REFERRAL_FORM_MESSAGES.repeat);
    if (v.trigger === 'INSTALLMENT_STREAK')
        check(isRepeat(v.installmentStreak), 'installmentStreak', REFERRAL_FORM_MESSAGES.repeat);
    if (triggerHasPurchase(v.trigger))
        check(optional(v.minAmount, isMoney), 'minAmount', REFERRAL_FORM_MESSAGES.money);
    check(optional(v.windowDays, isCount), 'windowDays', REFERRAL_FORM_MESSAGES.count);
    check(
        optional(v.maxRewardsPerReferrer, isCount),
        'maxRewardsPerReferrer',
        REFERRAL_FORM_MESSAGES.count,
    );
    check(isCount(v.maxRewardsPerReferral), 'maxRewardsPerReferral', REFERRAL_FORM_MESSAGES.count);
    issues.push(...rewardIssues(v.referrerReward, 'referrerReward', v.trigger));
    if (v.referredEnabled) {
        issues.push(...rewardIssues(v.referredReward, 'referredReward', v.trigger));
        check(isCount(v.maxReferredRewards), 'maxReferredRewards', REFERRAL_FORM_MESSAGES.count);
    }
    check(isBudgetAmount(v.budgetMax), 'budgetMax', REFERRAL_FORM_MESSAGES.money);
    if (!v.permanent && v.validUntil === '')
        issues.push([['validUntil'], REFERRAL_FORM_MESSAGES.validUntil]);
    else if (!v.permanent && v.validFrom !== '' && v.validUntil < v.validFrom)
        issues.push([['validUntil'], REFERRAL_FORM_MESSAGES.range]);
    const costCenter = costCenterError(v.costCenterId);
    if (costCenter) issues.push([['costCenterId'], costCenter]);
    return issues;
}

function emptyReward(type: ReferralRewardType): ReferralRewardForm {
    return {
        type,
        amount: '',
        percent: '',
        maxAmount: '',
        couponType: 'PERCENTAGE',
        couponValue: '',
        couponDays: '30',
    };
}

export function emptyReferralPromotionForm(channel: ReferralChannel): ReferralPromotionFormValues {
    const isSale = channel === 'SALE';
    return {
        channel,
        name: '',
        customerText: '',
        trigger: isSale ? 'FIRST_PURCHASE' : 'CREDIT_ACTIVATED',
        purchaseNumber: '2',
        installmentStreak: '3',
        payWith: 'ANY',
        cashMethods: [],
        financiers: [],
        customPaymentOptions: [],
        minAmount: '',
        windowDays: '',
        maxRewardsPerReferrer: '',
        maxRewardsPerReferral: '1',
        maxReferredRewards: '1',
        productCategories: [],
        tiers: [],
        storeIds: [],
        segmentIds: [],
        requiresActiveSurekeepCredit: false,
        referrerReward: emptyReward(isSale ? 'PERCENT_BONUS' : 'FIXED_BONUS'),
        referredEnabled: false,
        referredReward: emptyReward(isSale ? 'SALE_DISCOUNT' : 'FIXED_BONUS'),
        budgetMax: '',
        validFrom: '',
        permanent: false,
        validUntil: '',
        costCenterId: '',
    };
}

/** La promo de referido no lleva palancas: el back rechaza cualquiera prendida. */
const NO_LEVERS: SalePromotionLeversInput = {
    deviceDiscount: null,
    accessoryDiscount: null,
    gift: null,
    coupon: null,
    airtime: null,
    extendedWarranty: null,
    walletBonus: null,
};

const toBps = (percent: string) => Math.round(Number(percent) * 100);
const centsOrNull = (pesos: string) => (pesos.trim() === '' ? null : toCents(pesos));
const intOrNull = (text: string) => (text.trim() === '' ? null : Number(text));
/** Solo viaja si es más de 1: ausente vale 1 en el back. */
const timesAbove1 = (text: string): number | undefined =>
    Number(text) > 1 ? Number(text) : undefined;
const listOrNull = <T>(list: readonly T[]): T[] | null => (list.length > 0 ? [...list] : null);

function toRewardInput(reward: ReferralRewardForm): ReferralRewardInput {
    switch (reward.type) {
        case 'FIXED_BONUS':
            return { type: reward.type, amountCents: toCents(reward.amount) };
        case 'PERCENT_BONUS':
        case 'SALE_DISCOUNT':
            return {
                type: reward.type,
                percentBps: toBps(reward.percent),
                maxAmountCents: centsOrNull(reward.maxAmount),
            };
        case 'COUPON':
            return {
                type: reward.type,
                coupon: {
                    ...discountValue(reward.couponType, reward.couponValue),
                    validityDays: Number(reward.couponDays),
                    scope: 'CHAIN',
                },
            };
    }
}

/** Formas de pago de la promo; `null` = cualquiera. */
function toPaymentOptions(v: ReferralPromotionFormValues): PromotionPaymentOption[] | null {
    if (v.channel !== 'SALE' || !triggerHasPurchase(v.trigger)) return null;
    switch (v.payWith) {
        case 'ANY':
            return null;
        case 'SUREKEEP':
            return ['SUREKEEP'];
        case 'CASH':
            return v.cashMethods.length > 0 ? [...v.cashMethods] : [...CASH_METHODS];
        case 'OTHER':
            return v.financiers.length > 0 ? [...v.financiers] : [...FINANCIERS];
        case 'CUSTOM':
            return [...v.customPaymentOptions];
    }
}

/** Body completo; editar manda el mismo body como parche y reemplaza el bloque `referral`. */
export function toReferralPromotionRequest(
    v: ReferralPromotionFormValues,
    mode: 'create' | 'update' = 'create',
): CreateSalePromotionRequest {
    const purchase = triggerHasPurchase(v.trigger);
    const saleFilters = purchase && v.channel === 'SALE';
    const perReferral = timesAbove1(v.maxRewardsPerReferral);
    const referredTimes = v.referredEnabled ? timesAbove1(v.maxReferredRewards) : undefined;
    const referral: ReferralPromotionInput = {
        channel: v.channel,
        trigger: v.trigger,
        purchaseNumber: v.trigger === 'NTH_PURCHASE' ? Number(v.purchaseNumber) : null,
        installmentStreak: v.trigger === 'INSTALLMENT_STREAK' ? Number(v.installmentStreak) : null,
        minAmountCents: purchase ? centsOrNull(v.minAmount) : null,
        windowDays: intOrNull(v.windowDays),
        maxRewardsPerReferrer: intOrNull(v.maxRewardsPerReferrer),
        ...(perReferral !== undefined && { maxRewardsPerReferral: perReferral }),
        ...(referredTimes !== undefined && { maxReferredRewards: referredTimes }),
        referrerReward: toRewardInput(v.referrerReward),
        referredReward: v.referredEnabled ? toRewardInput(v.referredReward) : null,
    };
    return {
        name: v.name.trim(),
        customerText: v.customerText.trim(),
        levers: NO_LEVERS,
        scope: {
            productCategories: saleFilters ? listOrNull(v.productCategories) : null,
            tiers: saleFilters ? listOrNull(v.tiers) : null,
            skus: null,
            storeIds: listOrNull(v.storeIds),
        },
        segmentation: {
            segmentIds: listOrNull(v.segmentIds),
            requiresActiveSurekeepCredit: v.requiresActiveSurekeepCredit,
        },
        paymentOptions: toPaymentOptions(v),
        budgetMaxCents: budgetCents(v.budgetMax),
        validFrom: v.validFrom === '' ? null : mxDayStartUtc(v.validFrom),
        validUntil: v.permanent || v.validUntil === '' ? null : mxDayEndUtc(v.validUntil),
        referral,
        ...(mode === 'update'
            ? { costCenterId: costCenterForUpdate(v.costCenterId) }
            : costCenterForCreate(v.costCenterId)),
    };
}

const hundredthsOrEmpty = (value: number | null) => (value === null ? '' : fromHundredths(value));

function rewardFormOf(reward: ReferralReward): ReferralRewardForm {
    const coupon = reward.coupon;
    const couponRaw = coupon
        ? coupon.type === 'AMOUNT'
            ? coupon.amountCents
            : coupon.percentBps
        : null;
    return {
        ...emptyReward(reward.type),
        amount: hundredthsOrEmpty(reward.amountCents),
        percent: hundredthsOrEmpty(reward.percentBps),
        maxAmount: hundredthsOrEmpty(reward.maxAmountCents),
        couponType: coupon?.type ?? 'PERCENTAGE',
        couponValue: hundredthsOrEmpty(couponRaw ?? null),
        couponDays: coupon ? String(coupon.validityDays) : '30',
    };
}

const isSubsetOf = <T extends string>(list: readonly string[], of: readonly T[]): list is T[] =>
    list.every((item) => (of as readonly string[]).includes(item));

/** Una lista con todos los medios cuenta como «sin elegir». */
function payWithOf(
    options: readonly PromotionPaymentOption[] | null,
): Pick<
    ReferralPromotionFormValues,
    'payWith' | 'cashMethods' | 'financiers' | 'customPaymentOptions'
> {
    const list = options ?? [];
    const none = { cashMethods: [], financiers: [], customPaymentOptions: [] };
    if (list.length === 0) return { payWith: 'ANY', ...none };
    if (list.length === 1 && list[0] === 'SUREKEEP') return { payWith: 'SUREKEEP', ...none };
    if (isSubsetOf(list, CASH_METHODS))
        return {
            payWith: 'CASH',
            ...none,
            cashMethods: list.length === CASH_METHODS.length ? [] : list,
        };
    if (isSubsetOf(list, FINANCIERS))
        return {
            payWith: 'OTHER',
            ...none,
            financiers: list.length === FINANCIERS.length ? [] : list,
        };
    return { payWith: 'CUSTOM', ...none, customPaymentOptions: [...list] };
}

export function referralFormValuesFromPromotion(promo: SalePromotion): ReferralPromotionFormValues {
    const config: ReferralPromotionConfig | null | undefined = promo.referral;
    const channel = config?.channel ?? 'SALE';
    const empty = emptyReferralPromotionForm(channel);
    const costCenterId = costCenterText(promo.costCenterId);
    if (!config)
        return { ...empty, name: promo.name, customerText: promo.customerText, costCenterId };
    return {
        ...empty,
        ...payWithOf(promo.paymentOptions ?? null),
        name: promo.name,
        customerText: promo.customerText,
        trigger: config.trigger,
        purchaseNumber:
            config.purchaseNumber === null ? empty.purchaseNumber : String(config.purchaseNumber),
        installmentStreak:
            config.installmentStreak === null
                ? empty.installmentStreak
                : String(config.installmentStreak),
        minAmount: hundredthsOrEmpty(config.minAmountCents),
        windowDays: config.windowDays === null ? '' : String(config.windowDays),
        maxRewardsPerReferrer:
            config.maxRewardsPerReferrer === null ? '' : String(config.maxRewardsPerReferrer),
        maxRewardsPerReferral: String(config.maxRewardsPerReferral ?? 1),
        maxReferredRewards: String(config.maxReferredRewards ?? 1),
        productCategories: promo.scope.productCategories ?? [],
        tiers: promo.scope.tiers ?? [],
        storeIds: promo.scope.storeIds ?? [],
        segmentIds: promo.segmentation.segmentIds ?? [],
        requiresActiveSurekeepCredit: promo.segmentation.requiresActiveSurekeepCredit,
        referrerReward: rewardFormOf(config.referrerReward),
        referredEnabled: config.referredReward !== null,
        referredReward: config.referredReward
            ? rewardFormOf(config.referredReward)
            : empty.referredReward,
        budgetMax: hundredthsOrEmpty(promo.budgetMaxCents),
        validFrom: promo.validFrom === null ? '' : mxDay(promo.validFrom),
        permanent: promo.validUntil === null,
        validUntil: promo.validUntil === null ? '' : mxDay(promo.validUntil),
        costCenterId,
    };
}
